/**
 * provisioningService.ts
 * Frontend service for SGP.32 eSIM provisioning operations
 */

import { auth } from '@/config/firebase';
import { FUNCTIONS_URL } from '@/config/env';

const API_URL = FUNCTIONS_URL;

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  const token = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
}

async function apiGet(path: string) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api${path}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || err.message || `API error ${response.status}`);
  }
  return response.json();
}

async function apiPost(path: string, body?: any) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || err.message || `API error ${response.status}`);
  }
  return response.json();
}

async function apiPut(path: string, body: any) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || err.message || `API error ${response.status}`);
  }
  return response.json();
}

export const provisioningService = {
  // ── Vendor Config ──

  async getAvailableVendors(): Promise<string[]> {
    const data = await apiGet('/provisioning/vendors');
    return data.vendors;
  },

  async getVendorConfig(orgId: string) {
    try {
      const data = await apiGet(`/provisioning/config/${orgId}`);
      return data.config;
    } catch {
      return null;
    }
  },

  async saveVendorConfig(config: {
    organizationId: string;
    vendorId: string;
    vendorName?: string;
    baseUrl: string;
    credentialsSecretId: string;
    metadata?: Record<string, any>;
  }) {
    return apiPost('/provisioning/config', config);
  },

  async healthCheck(configId: string) {
    return apiPost(`/provisioning/config/${configId}/health-check`);
  },

  // ── EID Discovery ──

  async discoverEid(deviceId: string, modemImei: string): Promise<{ success: boolean; eid?: string; message: string }> {
    return apiPost(`/provisioning/discover-eid/${deviceId}/${modemImei}`);
  },

  // ── Provisioning Operations ──

  async pushProfile(params: {
    organizationId: string;
    esimProfileId: string;
    eid: string;
    targetState?: 'INSTALLED' | 'ENABLED';
    timeoutMs?: number;
  }) {
    return apiPost('/provisioning/push', params);
  },

  async setProfileState(iccid: string, organizationId: string, targetState: string) {
    return apiPut(`/provisioning/profiles/${iccid}/state`, { organizationId, targetState });
  },

  async deprovision(organizationId: string, iccid: string, targetState?: string) {
    return apiPost('/provisioning/deprovision', { organizationId, iccid, targetState });
  },

  async getDeviceProfiles(eid: string, organizationId: string) {
    const data = await apiGet(`/provisioning/device/${eid}/profiles?organizationId=${organizationId}`);
    return data.profiles;
  },

  async getDeviceAuditTrail(eid: string, organizationId: string) {
    const data = await apiGet(`/provisioning/device/${eid}/audit-trail?organizationId=${organizationId}`);
    return data.trail;
  },

  // ── Jobs ──

  async getJobs(orgId: string) {
    const data = await apiGet(`/provisioning/jobs/${orgId}`);
    return data.jobs;
  },

  async getJob(jobId: string) {
    const data = await apiGet(`/provisioning/jobs/detail/${jobId}`);
    return data.job;
  },
};
